import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../auth/AuthContext'
import { apiFetch } from '../api/client'
import type {
  AmpChangeRequest,
  AmpPreview,
  AmpState,
  BillingURL,
  ChangePlanRequest,
  CheckoutSessionRequest,
  PlanPreview,
  PortalSessionRequest,
  UpdateAmpRequest,
} from '../api/types'

// useCheckoutSession starts a Stripe Checkout flow. Returns the Stripe
// URL; the caller is responsible for the browser redirect.
export function useCheckoutSession() {
  const { idTokenProvider } = useAuth()
  return useMutation({
    mutationFn: (body: CheckoutSessionRequest) =>
      apiFetch<BillingURL>('/v1/me/billing/checkout-session', idTokenProvider, {
        method: 'POST',
        body,
      }),
  })
}

// usePortalSession opens the Stripe Customer Portal for plan/payment
// management. Requires the owner to already have a Stripe customer
// (i.e. has previously completed a Checkout); console-api 404s
// otherwise.
export function usePortalSession() {
  const { idTokenProvider } = useAuth()
  return useMutation({
    mutationFn: (body: PortalSessionRequest) =>
      apiFetch<BillingURL>('/v1/me/billing/portal', idTokenProvider, {
        method: 'POST',
        body,
      }),
  })
}

// useRefreshMe is sugar for invalidating the /v1/me query after a
// Checkout return — the Billing page calls it when ?status=success
// appears in the URL so flags flip without a full reload.
export function useRefreshMe() {
  const qc = useQueryClient()
  return () => qc.invalidateQueries({ queryKey: ['me'] })
}

// usePreviewPlan computes what an in-app plan change would do (switch with an
// exact prorated figure, or a cancel-at-period-end downgrade) for the
// confirmation dialog. Does not mutate the subscription.
export function usePreviewPlan() {
  const { idTokenProvider } = useAuth()
  return useMutation({
    mutationFn: (body: ChangePlanRequest) =>
      apiFetch<PlanPreview>('/v1/me/billing/plan/preview', idTokenProvider, {
        method: 'POST',
        body,
      }),
  })
}

// useChangePlan applies a base-plan change for an active subscriber (Beam↔Flood
// price swap, or a downgrade to Spark at period end). Returns no body (204); on
// success it invalidates /v1/me + the usage views so the new tier surfaces once
// the Stripe webhook has reconciled it.
export function useChangePlan() {
  const { idTokenProvider } = useAuth()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: ChangePlanRequest) =>
      apiFetch<void>('/v1/me/billing/plan', idTokenProvider, {
        method: 'POST',
        body,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me'] })
      qc.invalidateQueries({ queryKey: ['account-usage'] })
    },
  })
}

// useResumeSubscription clears a scheduled downgrade (keeps the paid plan). 204;
// invalidates /v1/me so the scheduled-downgrade banner clears.
export function useResumeSubscription() {
  const { idTokenProvider } = useAuth()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      apiFetch<void>('/v1/me/billing/resume', idTokenProvider, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] }),
  })
}

// useAmpPacks fetches the Amp storage packs currently attached to the owner's
// active subscription (GET /v1/me/billing/amp). Empty for a free/inactive
// owner. Gated on `enabled` so we don't fetch until the owner is known to be
// on an active paid plan (the Add controls are disabled otherwise).
export function useAmpPacks(enabled: boolean) {
  const { idTokenProvider, user } = useAuth()
  return useQuery({
    queryKey: ['billing-amp'],
    queryFn: ({ signal }) => apiFetch<AmpState>('/v1/me/billing/amp', idTokenProvider, { signal }),
    enabled: !!user && enabled,
    staleTime: 30_000,
  })
}

// usePreviewAmp computes the exact prorated amount a pending Amp change would
// charge (or credit) right now, for the confirmation dialog. Does not mutate
// the subscription. The returned proration_date is fed back into useUpdateAmp
// so the actual charge matches the quote.
export function usePreviewAmp() {
  const { idTokenProvider } = useAuth()
  return useMutation({
    mutationFn: (body: AmpChangeRequest) =>
      apiFetch<AmpPreview>('/v1/me/billing/amp/preview', idTokenProvider, {
        method: 'POST',
        body,
      }),
  })
}

// useUpdateAmp adds/removes an Amp pack by setting its desired quantity (0
// removes it), billing the proration immediately. On success it primes the amp
// query with the returned state and invalidates /v1/me + the usage views so the
// new caps surface once the Stripe webhook has reconciled them.
export function useUpdateAmp() {
  const { idTokenProvider } = useAuth()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: UpdateAmpRequest) =>
      apiFetch<AmpState>('/v1/me/billing/amp', idTokenProvider, {
        method: 'POST',
        body,
      }),
    onSuccess: (state) => {
      qc.setQueryData(['billing-amp'], state)
      qc.invalidateQueries({ queryKey: ['me'] })
      qc.invalidateQueries({ queryKey: ['account-usage'] })
    },
  })
}
